import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/useToast';
import { api, toastError } from '../utils/api';
import { r2Url } from '../utils/r2Url';
import type {
  Project,
  ProjectListResponse,
  CreateProjectRequest,
} from '@historyjki/shared';
import { WIZARD_STAGES, WIZARD_STAGE_LABELS } from '@historyjki/shared';
import { Badge } from '../components/ui/Badge';
import { Chip } from '../components/ui/Chip';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { AppHeader } from '../components/AppHeader';

type FilterType = 'all' | 'in_progress' | 'exported';

const STAGE_INDEX: Record<string, number> = {};
WIZARD_STAGES.forEach((s, i) => { STAGE_INDEX[s] = i; });

const STAGE_BADGE_VARIANT: Record<string, 'default' | 'terra' | 'sage' | 'plum' | 'outline'> = {
  capture: 'default',
  define: 'plum',
  compose: 'terra',
  export: 'sage',
};

function StoryCardProgress({ stage }: { stage: string }) {
  const total = WIZARD_STAGES.length;
  const current = STAGE_INDEX[stage] ?? 0;
  return (
    <div className="progress">
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          className={
            'progress__seg' +
            (i < current ? ' progress__seg--done' : i === current ? ' progress__seg--current' : '')
          }
        />
      ))}
    </div>
  );
}

export function DashboardPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [filter, setFilter] = useState<FilterType>('all');

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.get<ProjectListResponse>('/projects');
      setProjects(data.projects);
    } catch (err) {
      toastError(showToast, err, 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleNewProject = async () => {
    setCreating(true);
    try {
      const data = await api.post<{ project: Project }>('/projects', {
        title: 'Untitled Story',
      } as CreateProjectRequest);
      navigate(`/project/${data.project.id}`);
    } catch (err) {
      toastError(showToast, err, 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/projects/${deleteTarget.id}`);
      setProjects((prev) => prev.filter((p) => p.id !== deleteTarget.id));
      showToast('Project deleted', 'success');
      setDeleteTarget(null);
    } catch (err) {
      toastError(showToast, err, 'Failed to delete project');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const filteredProjects = useMemo(() => {
    if (filter === 'in_progress') return projects.filter((p) => p.stage !== 'export');
    if (filter === 'exported') return projects.filter((p) => p.stage === 'export');
    return projects;
  }, [projects, filter]);

  const lastEdited = useMemo(() => {
    if (projects.length === 0) return null;
    return projects.reduce((latest, p) =>
      p.updated_at > latest.updated_at ? p : latest
    ).updated_at;
  }, [projects]);

  const isFirstLoad = loading && projects.length === 0;
  const isEmpty = !loading && projects.length === 0;

  return (
    <div className="dash">
      <AppHeader />

      <div className="dash__main">
        {/* Hero */}
        <div className="dash__hero">
          <div>
            <div className="dash__hero-eyebrow">
              Studio · {loading ? '…' : `${projects.length} ${projects.length === 1 ? 'project' : 'projects'}`}
            </div>
            <h1>Your <em>shelf</em>, today.</h1>
          </div>
          <div className="dash__hero-meta">
            <div>
              <div>Total stories</div>
              <div className="dash__hero-meta-val">{loading ? '—' : projects.length}</div>
            </div>
            {lastEdited && (
              <div>
                <div>Last edited</div>
                <div className="dash__hero-meta-val" style={{ fontSize: '20px' }}>
                  {formatDate(lastEdited)}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Filter bar */}
        <div className="dash__bar">
          <span className="dash__bar-label">Filter</span>
          <div className="dash__filters">
            <Chip selected={filter === 'all'} onClick={() => setFilter('all')}>All</Chip>
            <Chip selected={filter === 'in_progress'} onClick={() => setFilter('in_progress')}>In progress</Chip>
            <Chip selected={filter === 'exported'} onClick={() => setFilter('exported')}>Exported</Chip>
          </div>
          <div className="dash__bar-spacer" />
        </div>

        {/* Grid */}
        {isFirstLoad ? (
          <div className="dash__grid">
            {[1, 2, 3].map((i) => (
              <div key={i} className="story-card">
                <div className="story-card__cover">
                  <div className="story-card__cover-placeholder" />
                </div>
                <div className="story-card__body">
                  <div className="dash__skeleton" style={{ width: '70%', height: '18px' }} />
                  <div className="dash__skeleton" style={{ width: '45%', height: '12px' }} />
                </div>
              </div>
            ))}
          </div>
        ) : isEmpty ? (
          <div className="dash__empty">
            <span className="dash__empty-icon">📚</span>
            <h3 className="dash__empty-title">No stories yet</h3>
            <p className="dash__empty-message">
              Create your first story and turn it into a beautiful illustrated book.
            </p>
            <button className="story-card--new" onClick={handleNewProject} disabled={creating}>
              <div className="story-card--new__plus">+</div>
              <div className="story-card--new__label">New blank story</div>
              <div className="story-card--new__sub">Start from a paste, an upload, or a single sentence.</div>
            </button>
          </div>
        ) : (
          <div className="dash__grid">
            {filteredProjects.length === 0 ? (
              <div className="dash__no-results">No stories match this filter.</div>
            ) : (
              filteredProjects.map((project) => (
                <div
                  key={project.id}
                  className="story-card"
                  onClick={() => navigate(`/project/${project.id}`)}
                >
                  <div className="story-card__cover">
                    {project.cover_image_r2 ? (
                      <img
                        src={r2Url(project.cover_image_r2)}
                        alt={project.title}
                      />
                    ) : (
                      <div className="story-card__cover-placeholder" />
                    )}
                    <div className="story-card__cover-bind" />
                    <div className="story-card__cover-title">{project.title}</div>
                  </div>
                  <div className="story-card__body">
                    <div className="story-card__title">{project.title}</div>
                    <div className="story-card__meta">
                      <Badge variant={STAGE_BADGE_VARIANT[project.stage] || 'default'} dot>
                        {WIZARD_STAGE_LABELS[project.stage]}
                      </Badge>
                      <span className="story-card__meta-dot" />
                      <span>{formatDate(project.updated_at)}</span>
                    </div>
                    <StoryCardProgress stage={project.stage} />
                    <button
                      className="story-card__delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTarget(project);
                      }}
                      aria-label={`Delete ${project.title}`}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
            <button
              className="story-card--new"
              onClick={handleNewProject}
              disabled={creating}
            >
              <div className="story-card--new__plus">+</div>
              <div className="story-card--new__label">New blank story</div>
              <div className="story-card--new__sub">Start from a paste, an upload, or a single sentence.</div>
            </button>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Project"
        message={`Are you sure you want to delete "${deleteTarget?.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
